import { useState, useEffect, useRef } from "react";

import { Input, Button, TrashHollowSVG } from "@newsela/angelou";
import PropTypes from "prop-types";
import { withTrackEvent, objectTypes, actionPrefixes } from "@newsela/snitch";

import usePollContext from "@/view/utils/usePollContext";
import {
  pollEditOptionsCss,
  pollFormCss,
  questionPlaceholderCss,
  responseFieldWrapperCss,
  trashButtonCss,
  trashButtonIconCss,
} from "./PollForm.style";
import ResponseInput from "./ResponseInput/ResponseInput";

const propTypes = {
  activePoll: PropTypes.object,
  cancelEditsButtonSlot: PropTypes.node,
  editingYourPollText: PropTypes.node,
  isValidateFormFieldsActive: PropTypes.bool,
  renderBlankForm: PropTypes.bool,
};

const MIN_NUMBER_OF_RESPONSES = 2;
const DEFAULT_NUMBER_OF_RESPONSES = 3;
const MAX_NUMBER_OF_RESPONSES = 5;
const DEFAULT_RESPONSE_ERROR = "Add another response.";
const FIRST_RESPONSE_ERROR = "Add a response. Try text and emojis.";
const sharedTrackingProps = {
  actionPrefix: actionPrefixes.initiate,
  objectType: objectTypes.poll,
};

const ButtonWithTrackEvent = withTrackEvent(Button);
const InputWithTrackEvent = withTrackEvent(Input);

export const PollForm = ({
  activePoll,
  cancelEditsButtonSlot,
  editingYourPollText,
  isValidateFormFieldsActive,
  renderBlankForm,
}) => {
  const { setCustomPoll } = usePollContext();
  const [pollQuestionValue, setPollQuestionValue] = useState("");
  const [pollQuestionError, setPollQuestionError] = useState(null);
  const [pollQuestionTouched, setPollQuestionTouched] = useState(false);
  const [pollResponseInputs, setResponseInputs] = useState([]);
  const pollResponseInputRef = useRef(null);
  const { data } = activePoll;
  const { choices, question } = data;

  const handleOnQuestionChange = (event) => {
    const newState = event.target.value;
    setPollQuestionValue(newState);
  };

  const handleOnResponseChange = (index, event) => {
    const newState = [...pollResponseInputs];
    newState[index].value = event.target.value;
    newState[index].error = null;
    setResponseInputs(newState);
  };

  const handleOnResponseBlur = (index) => {
    const newState = [...pollResponseInputs];

    if (!newState[index].touched) {
      newState[index].touched = true;
    }

    if (newState[index].value === "") {
      newState[index].error =
        index === 0 ? FIRST_RESPONSE_ERROR : DEFAULT_RESPONSE_ERROR;
    }

    setResponseInputs(newState);
  };

  const handleAddResponseInput = () => {
    const newState = {
      error: "",
      touched: false,
      value: "",
    };

    setResponseInputs([...pollResponseInputs, newState]);
    /**
     * When a new input is added, send focus to the new input
     * a timeout is necessary here to get at deeply nested elements https://stackoverflow.com/questions/35522220/react-ref-with-focus-doesnt-work-without-settimeout-my-example
     */
    setTimeout(() => {
      pollResponseInputRef?.current && pollResponseInputRef.current.focus();
    });
  };

  const handleRemoveResponseInput = (index) => {
    const newState = [...pollResponseInputs];
    newState.splice(index, 1);
    setResponseInputs(newState);
  };

  const allChoices = pollResponseInputs.map((input, index) => {
    return {
      choice_id: index + 1,
      text: input.value,
      voter_ids: [],
      votes: 0,
    };
  });

  const pollPayload = {
    question: pollQuestionValue,
    choices: [...allChoices],
  };

  useEffect(() => {
    if (pollQuestionValue === "") {
      setPollQuestionError("This poll needs a question.");
    } else {
      setPollQuestionError(null);
    }
  }, [pollQuestionValue, pollQuestionTouched]);

  useEffect(() => {
    const questionInputOnPageLoad = renderBlankForm ? "" : question;
    setPollQuestionValue(questionInputOnPageLoad);
  }, [activePoll, renderBlankForm]);

  useEffect(() => {
    const responseInputsOnPageLoad = [];
    const numberOfResponseInputsOnPageLoad = renderBlankForm
      ? DEFAULT_NUMBER_OF_RESPONSES
      : choices.length;
    for (let i = 0; i < numberOfResponseInputsOnPageLoad; i++) {
      responseInputsOnPageLoad.push({
        error: "",
        touched: false,
        value: renderBlankForm ? "" : choices[i].text,
      });
    }
    setResponseInputs(responseInputsOnPageLoad);
  }, [activePoll, renderBlankForm]);

  useEffect(() => {
    setCustomPoll(pollPayload);
  }, [JSON.stringify(pollPayload)]);

  useEffect(() => {
    if (isValidateFormFieldsActive) {
      setPollQuestionTouched(true);
      pollResponseInputs.forEach((i, index) => handleOnResponseBlur(index));
    }
  }, [isValidateFormFieldsActive]);

  return (
    <form css={pollFormCss}>
      <InputWithTrackEvent
        actionName="Add"
        dataProps={{ "data-qa-selector": "Poll-question-input-field" }}
        error={pollQuestionError}
        inputType={Input.inputTypes.text}
        label="Poll question"
        onBlur={() => {
          if (!pollQuestionTouched) {
            setPollQuestionTouched(true);
          }
        }}
        onChange={(event) => handleOnQuestionChange(event)}
        placeholder="What would you like to ask your students?"
        streamProperties={{
          description: "Add a poll question",
        }}
        touched={pollQuestionTouched}
        underInputId="poll-question"
        value={pollQuestionValue}
        __cssFor={{
          input: questionPlaceholderCss,
        }}
        {...sharedTrackingProps}
      />
      <div css={pollEditOptionsCss}>
        {editingYourPollText}
        {cancelEditsButtonSlot}
      </div>
      {pollResponseInputs?.map((input, index) => {
        const indexIfNoValue = input.value !== "" ? input.value : `#${index}`;
        return (
          <div css={responseFieldWrapperCss} key={index}>
            <ResponseInput
              error={input.error}
              index={index}
              onBlur={() => handleOnResponseBlur(index)}
              onChange={(event) => handleOnResponseChange(index, event)}
              ref={pollResponseInputRef}
              touched={input.touched}
              value={input.value}
            />
            {pollResponseInputs.length <= MIN_NUMBER_OF_RESPONSES ? null : (
              <ButtonWithTrackEvent
                actionName="Delete"
                ariaProps={{
                  "aria-label": `delete response option ${indexIfNoValue}`,
                }}
                dataProps={{
                  "data-qa-selector": `delete-response-option-${indexIfNoValue}`,
                }}
                flavor={Button.flavors.polarSecondary}
                icon={{ SvgComponent: TrashHollowSVG }}
                __cssFor={{
                  root: trashButtonCss,
                  Icon: {
                    root: trashButtonIconCss,
                  },
                }}
                onClick={() => handleRemoveResponseInput(index)}
                streamProperties={{
                  description: "Delete poll response option",
                }}
                {...sharedTrackingProps}
              />
            )}
          </div>
        );
      })}
      {pollResponseInputs?.length === MAX_NUMBER_OF_RESPONSES ? null : (
        <ButtonWithTrackEvent
          actionName="Add"
          dataProps={{ "data-qa-selector": "add-another-response-button" }}
          flavor={Button.flavors.tertiary}
          onClick={() => handleAddResponseInput()}
          streamProperties={{
            description: "Add another poll response option",
          }}
          {...sharedTrackingProps}
        >
          + Add another response
        </ButtonWithTrackEvent>
      )}
    </form>
  );
};

PollForm.propTypes = propTypes;
export default PollForm;
